import ArrowLeftThin from '@assets/icons/arrow-left-thin.svg';
import i18n from '@/utils/i18n';
import NavigationDownCircleIcon from '@assets/icons/navigation-down-circle.svg';
import OrderIcon from '@assets/icons/orders.svg';
import PhoneIcon from '@assets/icons/phone.svg';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { AnimatedSkeletonLoader } from '@/components/animated-skeleton-loader';
import { createStyles } from '@/contexts/theme-context';
import { Divider } from '@usepepper/ui/atoms/divider';
import { EmptyState } from '@/components/empty-state';
import { fennelTrack } from '@/analytics/fennel/hook';
import {
  FlatList,
  Platform,
  SafeAreaView,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import { formatCurrency, formatPercent } from '@/utils/money';
import {
  formatSummaryDate,
  formatTime,
  momentWithTimezone,
} from '@/utils/date';
import { INSIGHTS_HUB_ROUTES } from '@/navigators/routes';
import { InsightsHubStackParams } from '@/navigators/types';
import { ListEmptyState } from '@/components/list-empty-state';
import { OperatorEmployeeContact, OrderItemListFieldsFragment } from '@/types';
import { OrderItemDataType } from '@/components/orders/order-detail-list';
import { OrderLineItem } from '@/components/orders/order-line-item';
import { PageContainer } from '@/components/page-container';
import { PhonePickerBottomSheet } from '@/components/insights-hub/phone-picker-bottom-sheet';
import { SearchInput } from '@/components/search-input';
import { StackNavigationProp, StackScreenProps } from '@react-navigation/stack';
import { Typography } from '@usepepper/ui/atoms/typography';
import {
  useGetOrderForRestaurant,
  useGetOrderSummary,
} from '@/components/orders/hooks/use-get-order';
import { useHubCardNavigation } from '@/hooks/insights-hub/use-hub-card-navigation';
import { useMultiSearchFilters } from '@/hooks/use-multi-search-filters';
import { useNavigation } from '@react-navigation/native';
import { useSupplierDynamicConfigFlag } from '@/hooks/use-dynamic-config-value';
import { useUserContext } from '@/contexts/user-context';

type Props = StackScreenProps<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.OrderDetails
>;

type NavigationType = StackNavigationProp<
  InsightsHubStackParams,
  INSIGHTS_HUB_ROUTES.OrderDetails
>;

const getSearchableFields = (item: OrderItemDataType) => {
  const orderItem = item as OrderItemListFieldsFragment;
  return [
    orderItem.restaurant_display_name ?? '',
    orderItem.variants_pack.external_item_id ?? '',
    orderItem.pack.unit ?? '',
  ];
};

export const OrderDetailV2Screen: React.FC<Props> = ({ route }) => {
  const { goBack } = useNavigation<NavigationType>();
  const { navigateToItemDetails } = useHubCardNavigation();

  const [styles, theme] = useStyles();
  const { contacts, orderUUID, restaurantUUID, supplierUUID } = route.params;

  const { restaurantUUIDToRelationshipMap } = useUserContext();
  const restaurant = restaurantUUIDToRelationshipMap[restaurantUUID];
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );

  const [operatorContacts, setOperatorContacts] = useState<
    OperatorEmployeeContact[] | null
  >(null);

  const {
    loading: orderLoading,
    order,
    currencyCode,
    summary: { total, unitCount },
  } = useGetOrderForRestaurant(
    orderUUID,
    restaurant.chatUUIDs[0],
    restaurantUUID,
    supplierUUID,
  );
  const { grossProfit, loading: grossProfitLoading } =
    useGetOrderSummary(orderUUID);

  const { filteredData, onDisplayTextChange, searchText, searching } =
    useMultiSearchFilters<OrderItemDataType>({
      data: order?.orders_items ?? [],
      getSearchableFields,
    });

  useEffect(() => {
    fennelTrack(AnalyticEvent.ORDER_DETAIL_VIEW, {
      orderUUID,
      restaurantUUID,
      screenName: INSIGHTS_HUB_ROUTES.OrderDetails,
      supplierUUID,
    });
  }, [orderUUID, restaurantUUID, supplierUUID]);

  const onItemPress = useCallback(
    (item: OrderItemDataType) => {
      navigateToItemDetails(
        restaurantUUID,
        supplierUUID,
        item.variants_pack.uuid,
        INSIGHTS_HUB_ROUTES.OrderDetails,
      );
      fennelTrack(AnalyticEvent.ORDER_ITEM_PRESS, {
        isDraft: false,
        metadata: { dataSource: 'order' },
        orderItemUUID: item.uuid,
        orderUUID,
        origin: INSIGHTS_HUB_ROUTES.OrderDetails,
        restaurantUUID,
        supplierUUID,
        variantPackUUID: item.variants_pack.uuid,
      });
    },
    [navigateToItemDetails, orderUUID, restaurantUUID, supplierUUID],
  );

  const deliveryTime = useMemo(() => {
    if (!order) {
      return null;
    }

    return order.restaurant_desired_delivery_time
      ? formatSummaryDate(
          momentWithTimezone(order.restaurant_desired_delivery_time),
        )
      : null;
  }, [order]);

  const orderPlacedAtDate = useMemo(() => {
    if (!order) {
      return null;
    }

    return order.placed_at
      ? formatSummaryDate(momentWithTimezone(order.placed_at))
      : null;
  }, [order]);

  const orderPlacedAtTime = useMemo(() => {
    if (!order) {
      return null;
    }

    return order.placed_at
      ? formatTime(momentWithTimezone(order.placed_at))
      : null;
  }, [order]);

  const noSearchResults =
    searchText.length > 0 ? (
      <ListEmptyState
        text={i18n.t('no_results_for', {
          searchTerm: searchText,
        })}
      />
    ) : null;

  return (
    <PageContainer
      containerStyle={styles.container}
      navigationHeaderProps={{
        actions:
          !orderLoading && order && contacts && contacts.length > 0
            ? [
                {
                  accessibilityLabel: i18n.t(
                    'insights_hub.current_orders_section.call',
                  ),
                  Icon: () => (
                    <PhoneIcon
                      height={22}
                      stroke={theme.sk.color.icon.primary}
                      width={22}
                    />
                  ),
                  onPress: () => {
                    setOperatorContacts(contacts);
                    fennelTrack(AnalyticEvent.PHONE_PICKER_SHEET_OPEN, {
                      contacts,
                      orderUUID: order?.uuid!,
                      origin: INSIGHTS_HUB_ROUTES.OrderDetails,
                      platform: Platform.OS,
                      restaurantName: restaurant.name,
                      restaurantUUID: restaurant.uuid,
                    });
                  },
                },
              ]
            : undefined,
        BackButtonIcon: () => (
          <TouchableOpacity accessibilityRole="button" onPress={goBack}>
            <ArrowLeftThin fill={theme.sk.color.icon.primary} />
          </TouchableOpacity>
        ),
        showBackButton: true,
        showShadow: false,
      }}
    >
      <SafeAreaView style={styles.orderDetailsContainer}>
        {orderLoading ? (
          <View
            accessibilityState={{ busy: true }}
            style={styles.loadingContainer}
          >
            <ActivityIndicator />
          </View>
        ) : !order ? (
          <EmptyState
            description={i18n.t('order_detail.empty_state.description')}
            icon={<OrderIcon stroke={theme.sk.color.icon.primary} />}
            title={i18n.t('order_detail.empty_state.title')}
          />
        ) : (
          <>
            <ScrollView showsVerticalScrollIndicator={false}>
              <View style={styles.orderDetailsScrollableContent}>
                <View style={styles.orderHeaderContainer}>
                  <View>
                    <Typography
                      color={theme.sk.color.text.primary}
                      variant="paragraph-base"
                    >
                      {restaurant.name}
                    </Typography>
                    <Typography variant="heading-xl">
                      {`${i18n.t('deliver')} ${deliveryTime}`}
                    </Typography>
                  </View>
                  <View style={styles.detailsRow}>
                    <OrderIcon stroke={theme.sk.color.icon.primary} />
                    <Typography variant="paragraph-md">
                      {i18n.t('order_title', {
                        customerFacingId: order.customer_facing_id,
                      })}
                    </Typography>
                  </View>
                  {order.placed_by ? (
                    <View style={styles.detailsRow}>
                      <NavigationDownCircleIcon
                        stroke={theme.sk.color.icon.primary}
                      />
                      <Typography variant="paragraph-md">
                        {i18n.t('order_placed_by', {
                          date: orderPlacedAtDate,
                          name: order.placed_by.name,
                          time: orderPlacedAtTime,
                        })}
                      </Typography>
                    </View>
                  ) : null}
                </View>
                <SearchInput
                  autoCorrect={false}
                  containerStyle={styles.searchContainer}
                  isLoading={searching}
                  onChangeText={onDisplayTextChange}
                  placeholder={i18n.t('find_in_this_order')}
                  value={searchText}
                />
                <View>
                  <FlatList
                    data={filteredData}
                    ItemSeparatorComponent={() => (
                      <Divider style={styles.divider} />
                    )}
                    keyExtractor={(item) => item.uuid}
                    ListEmptyComponent={noSearchResults}
                    renderItem={({ item }) => (
                      <TouchableOpacity onPress={() => onItemPress(item)}>
                        <OrderLineItem
                          item={item}
                          orderCurrencyCode={currencyCode}
                        />
                      </TouchableOpacity>
                    )}
                    scrollEnabled={false}
                    showsVerticalScrollIndicator={false}
                  />
                  <Divider style={styles.lastDivider} />
                </View>
              </View>
            </ScrollView>
            <View style={styles.orderSummaryContainer}>
              {showGrossProfit && grossProfitLoading ? (
                <View style={styles.orderSummaryLine}>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-base"
                  >
                    {`${i18n.t('gross_profit')}`}
                  </Typography>
                  <AnimatedSkeletonLoader
                    height={16}
                    style={styles.grossProfitSkeleton}
                    width="25%"
                  />
                </View>
              ) : showGrossProfit && grossProfit ? (
                <View style={styles.orderSummaryLine}>
                  <Typography
                    color={theme.sk.color.text.tertiary}
                    variant="paragraph-base"
                  >
                    {i18n.t('gross_profit')}
                  </Typography>
                  <Typography
                    color={theme.sk.color.text.secondary}
                    variant="paragraph-base"
                  >
                    {[
                      formatPercent(grossProfit.percentage),
                      formatCurrency(
                        grossProfit.amount_in_micros,
                        grossProfit.currency_code,
                      ),
                    ].join(' • ')}
                  </Typography>
                </View>
              ) : null}
              <View style={styles.orderSummaryLine}>
                <Typography
                  color={theme.sk.color.text.primary}
                  variant="paragraph-base"
                >
                  {`${unitCount} ${unitCount === 1 ? i18n.t('pack_unit_unit') : i18n.t('units')}`}
                </Typography>
                <Typography variant="heading-md">{total}</Typography>
              </View>
            </View>
          </>
        )}
      </SafeAreaView>
      {operatorContacts && operatorContacts.length > 0 ? (
        <PhonePickerBottomSheet
          contacts={operatorContacts}
          onDismiss={() => setOperatorContacts(null)}
        />
      ) : null}
    </PageContainer>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  detailsRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  divider: {
    marginVertical: 12,
  },
  grossProfitSkeleton: {
    alignSelf: 'center',
  },
  lastDivider: {
    marginTop: 12,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  orderDetailsContainer: {
    flex: 1,
    gap: 16,
    justifyContent: 'space-between',
    marginHorizontal: 16,
  },
  orderDetailsScrollableContent: {
    gap: 16,
  },
  orderHeaderContainer: {
    gap: 4,
  },
  orderSummaryContainer: {
    gap: 8,
    marginBottom: theme.sk.spacing.spaceLarge,
  },
  orderSummaryLine: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  searchContainer: {
    backgroundColor: theme.sk.color.bg.tertiary.default,
    height: 48,
    marginBottom: 8,
  },
}));
